import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { FilterOutlined as FilterOutlineIcon, MoreOutlined as DotsHorizontalIcon } from '@ant-design/icons';
import { Button, Col, Dropdown, Input, Row } from 'antd';

import ModalActions from 'components/ModalActions';
import Spin from 'components/Spin';
import { EMPTY } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import type { Action, Group } from 'v2/interfaces/access-control';
import accessControlService from 'v2/services/access-control';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { AccessPolicyIcon } from './Icon';

type GroupsProps = {
  onEdit: (group: Group) => void;
};

type GroupPrivilege = {
  name: string;
  actions: Action[];
};

function getGroupPrivileges(group: Group): GroupPrivilege[] {
  return Object.values(group.privileges)
    .filter(privilege => privilege.actions.length > 0)
    .map(privilege => ({
      name: privilege.name,
      actions: privilege.actions
    }));
}

function Groups({ onEdit }: GroupsProps) {
  const [search, setSearch] = useState('');
  const [listGroupsTrigger, setListGroupsTrigger] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState({} as Group);

  const [groups] = useObservable(() => accessControlService.getGroups(), []);
  useObservable(() => accessControlService.loadGroups(), [listGroupsTrigger]);

  const [deleteGroup] = useCallbackObservable(groupId => {
    setIsDeleting(true);
    return accessControlService.deleteGroup(groupId).pipe(
      tap(() => {
        toast.success('Sucesso, grupo removido');
        setListGroupsTrigger(prev => !prev);
        handleCloseDeleteConfirmation();
      }),
      catchError(() => {
        toast.error('Erro ao remover grupo, tente novamente.');
        return EMPTY;
      }),
      finalize(() => {
        setIsDeleting(false);
      })
    );
  }, []);

  function handleEditOptionClick(group: Group) {
    onEdit(group);
  }

  function handleRemoveOptionClick(group: Group) {
    setCurrentGroup(group);
    setIsDeleteConfirmationOpen(true);
  }

  function handleCloseDeleteConfirmation() {
    setIsDeleteConfirmationOpen(false);
  }

  function handleDeleteGroup() {
    deleteGroup(currentGroup.id);
  }

  const filteredGroups = useMemo(
    () =>
      (groups || []).filter(group => (!search || search === '' ? group : group.name.toLowerCase().includes(search))),
    [groups, search]
  );

  const editPermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:editar');
  const deletePermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:excluir');
  const shouldShowActions = editPermission || deletePermission;

  return (
    <>
      <Row justify='end'>
        <Col xs={24} md={8}>
          <div className='max-sm:block md:-mt-[66px]'>
            <Input
              id='search-product-input'
              value={search}
              suffix={<FilterOutlineIcon />}
              onChange={e => setSearch(e.target.value)}
              placeholder='Pesquise pelo nome do grupo'
            />
          </div>
        </Col>
      </Row>

      <ModalActions
        open={isDeleteConfirmationOpen}
        onClose={handleCloseDeleteConfirmation}
        onOk={handleDeleteGroup}
        title='Atenção'
        buttonOk='Continuar'
        loading={isDeleting}
      >
        <p>Deseja realmente remover o grupo {currentGroup.name}?</p>
      </ModalActions>

      {groups === null ? (
        <div className='w-full text-center'>
          <Spin />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {filteredGroups?.length > 0 ? (
              filteredGroups?.map(group => {
                const groupPrivileges = getGroupPrivileges(group);
                return (
                  <Col xs={24} sm={12} md={8} key={group.id}>
                    <div className='relative h-full rounded-sm border border-gray-200 bg-white p-4 shadow-xs'>
                      {shouldShowActions && group.owner && (
                        <div className='absolute top-4 right-4'>
                          <Dropdown
                            menu={{
                              items: [
                                { key: 'edit', label: 'Editar', onClick: () => handleEditOptionClick(group) },
                                { key: 'remove', label: 'Remover', onClick: () => handleRemoveOptionClick(group) }
                              ]
                            }}
                            trigger={['click']}
                          >
                            <Button type='text' size='small' icon={<DotsHorizontalIcon style={{ fontSize: 18 }} />} />
                          </Dropdown>
                        </div>
                      )}
                      <h3 className='mr-4 mb-2 text-[1.2rem] font-bold'>{group.name}</h3>
                      <div className='flex flex-wrap gap-2'>
                        {groupPrivileges.map(privilege => (
                          <div key={privilege.name} className='basis-[calc(50%-16px)]'>
                            <p className='mb-1! text-sm font-medium'>{privilege.name}</p>
                            <ul className='flex list-none flex-wrap gap-2'>
                              {privilege.actions.map(action => (
                                <AccessPolicyIcon key={action.id} action={action} />
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                );
              })
            ) : (
              <Row gutter={16}>
                <Col span={24}>
                  <div className='p-4'>Nenhum registro encontrado</div>
                </Col>
              </Row>
            )}
          </Row>
        </>
      )}
    </>
  );
}

export { Groups };
